import PropTypes from 'prop-types';
import styles from './CodeVerifier.module.scss';
import React, { useEffect, useRef } from 'react';

export default function CodeVerifier({
  length,
  code,
  setCode,
  isLoading = false,
  type,
  isError = false
}) {
  const inputsRef = useRef([]);

  useEffect(() => {
    inputsRef.current = inputsRef.current.slice(0, length);
  }, [length]);

  const applyPastedDigits = (digits, startIndex) => {
    const digitsToUse = (digits || '')
      .replace(/\D/g, '')
      .slice(0, length - startIndex);
    if (!digitsToUse) return;
    const newCode = [...code];
    digitsToUse.split('').forEach((digit, i) => {
      newCode[startIndex + i] = digit;
    });
    setCode(newCode);
    const nextIndex = Math.min(startIndex + digitsToUse.length, length - 1);
    setTimeout(() => inputsRef.current[nextIndex]?.focus(), 0);
  };

  useEffect(() => {
    const handlePasteShortcut = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        const isCodeInput = inputsRef.current.some(
          el => el === document.activeElement
        );
        if (isCodeInput) {
          e.preventDefault();
          navigator.clipboard.readText().then(text => {
            const focusedIndex = inputsRef.current.findIndex(
              el => el === document.activeElement
            );
            const startIndex = focusedIndex >= 0 ? focusedIndex : 0;
            applyPastedDigits(text, startIndex);
          });
        }
      }
    };
    document.addEventListener('keydown', handlePasteShortcut);
    return () => document.removeEventListener('keydown', handlePasteShortcut);
  }, [code, length]);

  const handleChange = (e, index) => {
    const val = e.target.value;

    if (/^\d$/.test(val)) {
      const newCode = [...code];
      newCode[index] = val;
      setCode(newCode);

      if (index < length - 1) {
        inputsRef.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    const newCode = [...code];

    switch (e.key) {
      case 'Backspace':
        if (code[index]) {
          newCode[index] = '';
          setCode(newCode);
        } else if (index > 0) {
          newCode[index - 1] = '';
          setCode(newCode);
          inputsRef.current[index - 1]?.focus();
        }
        break;

      case 'ArrowLeft':
        if (index > 0) {
          const prevInput = inputsRef.current[index - 1];
          prevInput?.focus();
          setTimeout(() => prevInput?.select(), 0);
        }
        break;

      case 'ArrowRight':
        if (index < length - 1) {
          const nextInput = inputsRef.current[index + 1];
          nextInput?.focus();
          setTimeout(() => nextInput?.select(), 0);
        }
        break;

      case 'Tab':
        break;

      default:
        if (!/^\d$/.test(e.key)) {
          e.preventDefault();
        }
        break;
    }
  };

  const renderInput = (_, index) => (
    <React.Fragment key={`input_${index}`}>
      <input
        onPaste={e => {
          e.preventDefault();
          applyPastedDigits(e.clipboardData.getData('text'), index);
        }}
        id={`cv_${index}`}
        type='tel'
        inputMode='numeric'
        maxLength={1}
        autoComplete='one-time-code'
        className={`${styles.codeElement} ${isError ? styles.error : ''} ${
          isLoading ? styles.codeElementDisabled : ''
        }`}
        onFocus={e => e.target.select()}
        ref={el => (inputsRef.current[index] = el)}
        value={code[index]}
        style={{ fontSize: code[index] ? '1.5rem' : '1rem' }}
        onChange={e => handleChange(e, index)}
        onKeyDown={e => handleKeyDown(e, index)}
        disabled={isLoading}
      />
      {type === 'phone' && (index + 1 === 3 || index === 5) && (
        <p key={`dash_${index}`}>-</p>
      )}
    </React.Fragment>
  );

  return <div className={styles.codeContainer}>{code.map(renderInput)} </div>;
}

CodeVerifier.propTypes = {
  length: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  type: PropTypes.string,
  isError: PropTypes.bool,
  code: PropTypes.arrayOf(PropTypes.string).isRequired,
  setCode: PropTypes.func.isRequired
};
